import { call, getContext, put, select, takeLatest } from 'redux-saga/effects';
import {
  IInitiatePlacementMarginPayPacketStatusTransition,
  IUpdatePlacementMarginPayPackageStatusPayload,
  PlacementMarginStatusTransitionModalType,
} from './placement-pay-package-status.model';
import { PayloadAction } from '@reduxjs/toolkit';
import { placementMarginPayPackageStatusActions } from './placement-pay-package-status.redux';
import { getTransitionStatusCategory } from '@AMIEWEB/MarginTool/Components/PayPackage/helper';
import { MarginToolService } from 'app/services/MarginToolServices/margin-tool-service';
import { httpSuccess } from 'app/services/serviceHelpers';
import { trackException } from 'app-insights/appInsightsTracking';
import { ExceptionType } from 'app/enums/Common';
import { manuallyResetPromiseCounter, trackPromise } from 'react-promise-tracker';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { placementMarginScenarioActions } from '../placement-add-edit-scenario/add-edit-placement-scenario.redux';
import { fetchPlacementMarginToolDetails } from '../placement-add-edit-scenario/add-edit-placement-scenario.saga';
import { IEmailType } from 'app/models/Notification/Notification';
import { notificationSelection } from 'store/redux-store/notification/notification.selector';
import { notificationDataActions } from 'store/redux-store/notification/notification.redux';
import {
  IMarginUpdateResponse,
  IMarginUpdateResponseErrorTargetTypes,
  IMarginUpdateResponseErrorTypes,
} from '@AMIEWEB/MarginTool/Components/PayPackage/models/AddScenarioModal';
import { IBannerAction } from 'app/models/Global/Global';
import i18next from 'i18next';
import { PromiseTrackerKeys } from 'app/constants/PromiseTrackerKeys';

const TrackPayPackageStatusUpdate = (fn, ...args) =>
  trackPromise(fn(...args), PromiseTrackerKeys.placementMarginTool.updateScenarioStatus);
const TrackEmailRecipentsList = (fn, ...args) => trackPromise(fn(...args), 'get-email-receipents-list');

export function* initiatePayPacketStatusTransition(
  action: PayloadAction<IInitiatePlacementMarginPayPacketStatusTransition>,
) {
  try {
    yield put(placementMarginPayPackageStatusActions.setPayPackageStatusTransitionModalDetails(action.payload));
    const statusId = action.payload.statusId;
    const category = getTransitionStatusCategory(action.payload.type);
    const marginToolService: MarginToolService = yield getContext('marginToolService');
    const response = yield call(marginToolService.getlistofPayPackageStatus, statusId, category);
    if (httpSuccess(response.status)) {
      yield put(placementMarginPayPackageStatusActions.setPayPackageStatusOptions(response?.data));
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'initiatePayPacketStatusTransition',
        area: 'src/store/redux-store/pay-package-status/pay-package-status.saga.ts',
      },
    });
  }
}

export function* initiateUpdatePayPackageStatus(action: PayloadAction<IUpdatePlacementMarginPayPackageStatusPayload>) {
  try {
    yield put(placementMarginScenarioActions.setPlacementMarginScenarioCreateUpdateResponse(null));
    yield put(globalActions.closeBanner());

    const marginToolService: MarginToolService = yield getContext('marginToolService');
    const response = yield call(TrackPayPackageStatusUpdate, marginToolService.updatePayPackageStatus, action.payload);

    const scenarioResponse: IMarginUpdateResponse = response?.data;
    if (scenarioResponse) {
      scenarioResponse.target = IMarginUpdateResponseErrorTargetTypes.View;
    }

    yield put(placementMarginScenarioActions.setPlacementMarginScenarioCreateUpdateResponse(scenarioResponse));

    if (httpSuccess(response.status)) {
      if (!scenarioResponse?.inError) {
        yield put(
          placementMarginPayPackageStatusActions.setPayPackageStatusTransitionModalDetails({
            openModal: false,
            type: PlacementMarginStatusTransitionModalType.None,
          }),
        );
        yield put(
          globalActions.setSnackBar({
            message: scenarioResponse?.message,
            severity: 'success',
          }),
        );
        yield call(fetchPlacementMarginToolDetails, {
          payload: { placementId: action.payload.placementId },
          type: placementMarginScenarioActions.getPlacementMarginToolDetails.type,
        });
        yield put(placementMarginPayPackageStatusActions.setRecentStatusUpdatedScenarioId(action.payload.payPackageId));
        if (action.payload.openEmailModal) {
          const notificationData = yield select(notificationSelection);
          if (!notificationData.email.open && notificationData.email.minimized) {
            yield put(
              notificationDataActions.setEmailInteraction({
                open: true,
                minimized: false,
              }),
            );
            yield put(
              notificationDataActions.setSnackBarData({ channel: 'email', manual: false, changeWarning: true }),
            );
          } else {
            yield put(
              placementMarginPayPackageStatusActions.setStatusUpdatedEmailModalOpen({
                openEmailModal: true,
                emailType: IEmailType.Auto,
              }),
            );
          }
        }
      } else if (scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.BaseDetailsModifiedError) {
        yield put(placementMarginScenarioActions.setPlacementMarginIsOrderIdModifiedStatus(true));
        yield put(placementMarginScenarioActions.setPlacementMarginShowOrderIdModifiedDialogStatus(true));
      } else if (
        scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.TimeStampError ||
        scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.PerDiemError ||
        scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.DateError
      ) {
        yield put(
          placementMarginPayPackageStatusActions.setPayPackageStatusTransitionModalDetails({
            openModal: false,
            type: PlacementMarginStatusTransitionModalType.None,
          }),
        );
        if (scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.TimeStampError) {
          yield put(
            globalActions.setBanner({
              message: scenarioResponse?.message || i18next.t('common.failedLoad'),
              severity: 'error',
              justify: 'flex-start',
              action: IBannerAction.refreshPage,
              justifyActionContent: 'flex-end',
            }),
          );
        } else if (
          scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.PerDiemError ||
          scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.DateError
        ) {
          yield put(
            globalActions.setBanner({
              message: scenarioResponse?.message || i18next.t('common.failedLoad'),
              severity: 'error',
              justify: 'flex-start',
            }),
          );
        }
      } else {
        yield put(
          globalActions.setSnackBar({
            message: scenarioResponse?.message || i18next.t('common.failedLoad'),
            severity: 'error',
          }),
        );
      }
    } else {
      //http 400+
      if (scenarioResponse?.errorType === IMarginUpdateResponseErrorTypes.DateError) {
        yield put(
          placementMarginPayPackageStatusActions.setPayPackageStatusTransitionModalDetails({
            openModal: false,
            type: PlacementMarginStatusTransitionModalType.None,
          }),
        );
        yield put(
          globalActions.setBanner({
            message: scenarioResponse?.message || i18next.t('common.failedLoad'),
            severity: 'error',
            justify: 'flex-start',
          }),
        );
      } else {
        yield put(
          globalActions.setSnackBar({
            message: i18next.t('common.failedLoad'),
            severity: 'error',
          }),
        );
      }
    }
    manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.updateScenarioStatus);
  } catch (error) {
    manuallyResetPromiseCounter(PromiseTrackerKeys.placementMarginTool.updateScenarioStatus);
    yield put(
      globalActions.setSnackBar({
        message: i18next.t('common.failedLoad'),
        severity: 'error',
      }),
    );
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'initiatePayPacketStatusTransition',
        area: 'src/store/redux-store/pay-package-status/pay-package-status.saga.ts',
      },
    });
  }
}
export function* getEmailRecipientsList(action: PayloadAction<{ placementId: number }>) {
  try {
    const marginToolService: MarginToolService = yield getContext('marginToolService');

    const response = yield call(
      TrackEmailRecipentsList,
      marginToolService.getPlacementEmployeesForMargin,
      action.payload.placementId,
    );
    if (httpSuccess(response.status)) {
      yield put(placementMarginPayPackageStatusActions.setEmailRecipients(response?.data));
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.APIRequestError,
        functionName: 'getEmailRecipientsList',
        area: 'src/store/redux-store/pay-package-status/pay-package-status.saga.ts',
      },
    });
  }
}

export function* placementMarginPayPackageStatusSaga() {
  yield takeLatest(
    placementMarginPayPackageStatusActions.initiatePlacementMarginPayPacketStatusTransition.type,
    initiatePayPacketStatusTransition,
  );
  yield takeLatest(
    placementMarginPayPackageStatusActions.updatePlacementMarginPayPackageStatus.type,
    initiateUpdatePayPackageStatus,
  );
  yield takeLatest(
    placementMarginPayPackageStatusActions.getPlacementMarginEmailRecipientsList.type,
    getEmailRecipientsList,
  );
}
